import { useEffect, useState, useCallback } from 'react';
import { ColorPicker } from './ColorPicker';
import { useUnityBuildStore } from '../../stores/store';
import { mutateSelectedAssembly } from '../../api/MutateSelectedAssembly';
import { useSubmitTakeoffAssembly } from '../../hooks/useSubmitTakeoffAssembly';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import {
  Paper,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  useTheme,
  alpha,
  InputLabel,
  FormControl,
} from '@mui/material';

export const ColorAndStyleEditor = () => {
  const theme = useTheme();
  const setIsViewingColorEditor = useStore(useUnityBuildStore).setIsViewingColorEditor;
  const setIsEditingPointStyle = useStore(useUnityBuildStore).setIsEditingPointStyle;
  const setIsEditingPoint = useStore(useUnityBuildStore).setIsEditingPoint;
  const isEditingPointBorderColor = useStore(useUnityBuildStore).isEditingPointBorderColor;
  const isEditingPointFillColor = useStore(useUnityBuildStore).isEditingPointFillColor;
  const isEditingLineColor = useStore(useUnityBuildStore).isEditingLineColor;
  const userScale = useStore(useUnityBuildStore).userScale;
  const currentTakeoffAssembly = useStore(useAssemblyStore).currentTakeoffAssembly;
  const setCurrentTakeoffAssembly = useStore(useAssemblyStore).setCurrentTakeoffAssembly;

  const { submitAssembly } = useSubmitTakeoffAssembly();

  // Track pending changes to prevent server data from overwriting recent user changes
  const [pendingChanges, setPendingChanges] = useState<Map<string, any>>(new Map());
  const [lastChangeTime, setLastChangeTime] = useState<number>(0);

  // Track a change to prevent server data from overwriting recent user changes
  const trackChange = (property: string, value: any) => {
    const now = Date.now();
    setPendingChanges(prev => new Map(prev.set(property, value)));
    setLastChangeTime(now);
  };

  // Check if we should preserve local changes over server data
  const shouldPreserveLocalChanges = useCallback(() => {
    const now = Date.now();
    // Preserve local changes for 5 seconds after the last change (longer than server delay)
    return now - lastChangeTime < 5000;
  }, [lastChangeTime]);

  // Extended set of supported point shapes
  const pointTypes = [
    'circle',
    'square',
    'triangle',
    'diamond',
    'cross',
    'plus',
    'star',
    'hexagon',
  ];
  const line_dash_arrays = [
    {
      name: 'Solid line: ───────────',
      value: '',
    },
    { name: 'Dotted line: · · · · · · · · · · · ·', value: '1,1' },

    { name: 'Dash line: — — — — — ', value: '10,10' },
    { name: 'Dash-Dot line: — · — · — ', value: '5,5,1,5' },
  ];

  // Modified submit wrapper that preserves the current editing state
  const submitWithStatePreservation = async (
    assembly: TakeoffAssembly,
    options = { shouldUpdateBreakout: false, isUpdating: true }
  ) => {
    try {
      await submitAssembly({
        shouldUpdateBreakout: false,
        assembly: assembly,
        isUpdating: options.isUpdating,
        pageScale: userScale,
        shouldClearCurrentTakeoffAssembly: false,
      });

      // Preserve editing state by maintaining all the style flags
      setIsEditingPointStyle({
        isEditingPointBorderColor,
        isEditingPointFillColor,
        isEditingLineColor,
      });

      // Ensure the style editor stays open
      setIsEditingPoint({
        isEditingPoint: false,
        isEditingStyle: true,
        isViewingTakeoffEditor: false,
      });
    } catch (error) {
      console.error('Failed to save assembly style changes:', error);
      // Optionally show user feedback about the error
    }
  };

  const handleSetPointType = async (type: string) => {
    // Track this change to prevent server data from overwriting it
    trackChange('pointtype', type);

    // Optimistic update - immediately update the store for instant feedback
    if (currentTakeoffAssembly) {
      setCurrentTakeoffAssembly({
        ...currentTakeoffAssembly,
        pointtype: type,
      });
    }

    // Background API call
    const updatedAssembly = mutateSelectedAssembly({
      isEditingPointType: true,
      assembly: currentTakeoffAssembly,
      pointType: type,
    });

    submitWithStatePreservation(updatedAssembly).catch(console.error);
  };

  const handleSetPointSize = async (size: number) => {
    // Track this change to prevent server data from overwriting it
    trackChange('pointsize', size);

    // Optimistic update - immediately update the store for instant feedback
    if (currentTakeoffAssembly) {
      setCurrentTakeoffAssembly({
        ...currentTakeoffAssembly,
        pointsize: size,
      });
    }

    // Background API call
    const updatedAssembly = mutateSelectedAssembly({
      isEditingPointSize: true,
      assembly: currentTakeoffAssembly,
      size: size,
    });

    submitWithStatePreservation(updatedAssembly).catch(console.error);
  };

  const handleSetLineSize = async (size: number) => {
    // Track this change to prevent server data from overwriting it
    trackChange('linesize', size);

    // Optimistic update - immediately update the store for instant feedback
    if (currentTakeoffAssembly) {
      setCurrentTakeoffAssembly({
        ...currentTakeoffAssembly,
        linesize: size,
      });
    }

    // Background API call
    const updatedAssembly = mutateSelectedAssembly({
      isEditingLineSize: true,
      assembly: currentTakeoffAssembly,
      size: size,
    });

    submitWithStatePreservation(updatedAssembly).catch(console.error);
  };

  const handleSetline_dash_array = async (type: string) => {
    // Track this change to prevent server data from overwriting it
    trackChange('linedasharray', type);

    // Optimistic update - immediately update the store for instant feedback
    if (currentTakeoffAssembly) {
      setCurrentTakeoffAssembly({
        ...currentTakeoffAssembly,
        linedasharray: type,
      });
    }

    // Background API call
    const updatedAssembly = mutateSelectedAssembly({
      isEditinglineDashArray: true,
      assembly: currentTakeoffAssembly,
      lineDashArray: type,
    });

    submitWithStatePreservation(updatedAssembly).catch(console.error);
  };

  const handleClose = async () => {
    // Only close the style editor, don't affect the point editor state
    setIsViewingColorEditor(false);
    setIsEditingPoint({
      isEditingPoint: true,
      isViewingTakeoffEditor: false,
      isEditingStyle: false,
    });

    // Don't clear the currentTakeoffAssembly immediately to preserve changes
    // The assembly will be cleared when the user selects a different assembly
    // or when the component unmounts
    // setCurrentTakeoffAssembly(null);
  };

  const windowHeight = window.innerHeight;

  // Cleanup effect to clear assembly when component unmounts or when a new assembly is selected
  useEffect(() => {
    return () => {
      // Clear the current assembly when component unmounts to prevent memory leaks
      setCurrentTakeoffAssembly(null);
    };
  }, [setCurrentTakeoffAssembly]);

  // Monitor currentTakeoffAssembly changes and apply pending changes if needed
  useEffect(() => {
    if (!currentTakeoffAssembly || !shouldPreserveLocalChanges()) {
      return;
    }

    // Check if we have pending changes that should override server data
    let hasChanges = false;
    const updatedAssembly = { ...currentTakeoffAssembly };

    for (const [property, value] of pendingChanges.entries()) {
      if ((currentTakeoffAssembly as any)[property] !== value) {
        (updatedAssembly as any)[property] = value;
        hasChanges = true;
      }
    }

    // Apply pending changes if they differ from server data
    if (hasChanges) {
      setCurrentTakeoffAssembly(updatedAssembly);
    }
  }, [
    currentTakeoffAssembly,
    pendingChanges,
    shouldPreserveLocalChanges,
    setCurrentTakeoffAssembly,
  ]);

  // Clean up pending changes after they're no longer needed
  useEffect(() => {
    if (!shouldPreserveLocalChanges() && pendingChanges.size > 0) {
      // Clear pending changes after the grace period
      setPendingChanges(new Map());
    }
  }, [shouldPreserveLocalChanges, pendingChanges]);

  return (
    <Paper
      onContextMenu={event => event.preventDefault()}
      elevation={4}
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: 1,
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxHeight: `${windowHeight - 100}px`,
        overflowY: 'auto',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        '& .MuiFormControl-root': {
          width: '100%',
        },
      }}
    >
      {/* Title */}
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{
          textAlign: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          pb: 1,
        }}
      >
        Style Editor
      </Typography>

      {/* Count Shape */}
      <FormControl size="small">
        <InputLabel>Count Shape</InputLabel>
        <Select
          value={currentTakeoffAssembly?.pointtype || ''}
          onChange={e => handleSetPointType(e.target.value)}
          label="Count Shape"
        >
          {pointTypes.map(type => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Count Size */}
      <TextField
        label="Count Size"
        type="number"
        size="small"
        value={currentTakeoffAssembly?.pointsize || ''}
        onChange={e => {
          const value = Number(e.target.value);
          if (value > 50) {
            handleSetPointSize(50);
            alert('Value cannot exceed 50');
            return;
          }
          handleSetPointSize(value);
        }}
        inputProps={{ max: 50 }}
      />

      {/* Length Pattern Options */}
      {currentTakeoffAssembly?.assemblymeasurementtype === 'length' && (
        <>
          <FormControl size="small">
            <InputLabel>Length Pattern</InputLabel>
            <Select
              value={currentTakeoffAssembly?.linedasharray || ''}
              onChange={e => handleSetline_dash_array(e.target.value)}
              label="Length Pattern"
            >
              {line_dash_arrays.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Length Pattern Size"
            type="number"
            size="small"
            value={currentTakeoffAssembly?.linesize || ''}
            onChange={e => {
              const value = Number(e.target.value);
              if (value > 50) {
                handleSetLineSize(50);
                alert('Value cannot exceed 50');
                return;
              }
              handleSetLineSize(value);
            }}
            inputProps={{ max: 50 }}
          />

          <Button
            variant={isEditingLineColor ? 'contained' : 'outlined'}
            onClick={() =>
              setIsEditingPointStyle({
                isEditingLineColor: !isEditingLineColor,
                isEditingPointBorderColor: false,
                isEditingPointFillColor: false,
              })
            }
            sx={{
              backgroundColor: isEditingLineColor
                ? alpha(theme.palette.error.main, 0.1)
                : 'transparent',
              color: isEditingLineColor ? theme.palette.error.main : theme.palette.text.primary,
              borderColor: isEditingLineColor ? theme.palette.error.main : theme.palette.divider,
              '&:hover': {
                backgroundColor: isEditingLineColor
                  ? alpha(theme.palette.error.main, 0.2)
                  : alpha(theme.palette.action.hover, 0.1),
                borderColor: isEditingLineColor
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
              },
            }}
          >
            Set Line Color
          </Button>
        </>
      )}

      {/* Border Color Button */}
      <Button
        variant={isEditingPointBorderColor ? 'contained' : 'outlined'}
        onClick={() =>
          setIsEditingPointStyle({
            isEditingPointBorderColor: !isEditingPointBorderColor,
            isEditingPointFillColor: false,
            isEditingLineColor: false,
          })
        }
        sx={{
          backgroundColor: isEditingPointBorderColor
            ? alpha(theme.palette.error.main, 0.1)
            : 'transparent',
          color: isEditingPointBorderColor ? theme.palette.error.main : theme.palette.text.primary,
          borderColor: isEditingPointBorderColor ? theme.palette.error.main : theme.palette.divider,
          '&:hover': {
            backgroundColor: isEditingPointBorderColor
              ? alpha(theme.palette.error.main, 0.2)
              : alpha(theme.palette.action.hover, 0.1),
            borderColor: isEditingPointBorderColor
              ? theme.palette.error.main
              : theme.palette.primary.main,
          },
        }}
      >
        Set Border Color
      </Button>

      {/* Fill Color Button */}
      <Button
        variant={isEditingPointFillColor ? 'contained' : 'outlined'}
        onClick={() =>
          setIsEditingPointStyle({
            isEditingPointFillColor: !isEditingPointFillColor,
            isEditingPointBorderColor: false,
            isEditingLineColor: false,
          })
        }
        sx={{
          backgroundColor: isEditingPointFillColor
            ? alpha(theme.palette.error.main, 0.1)
            : 'transparent',
          color: isEditingPointFillColor ? theme.palette.error.main : theme.palette.text.primary,
          borderColor: isEditingPointFillColor ? theme.palette.error.main : theme.palette.divider,
          '&:hover': {
            backgroundColor: isEditingPointFillColor
              ? alpha(theme.palette.error.main, 0.2)
              : alpha(theme.palette.action.hover, 0.1),
            borderColor: isEditingPointFillColor
              ? theme.palette.error.main
              : theme.palette.primary.main,
          },
        }}
      >
        Set Fill Color
      </Button>

      <ColorPicker />

      {/* Close Button */}
      <Button
        variant="contained"
        color="error"
        onClick={handleClose}
        sx={{
          mt: 1,
          fontWeight: 500,
        }}
      >
        Close
      </Button>
    </Paper>
  );
};
